import React, {
  useState,
  createContext,
  Dispatch,
  SetStateAction,
  ContextType,
  useEffect,
  CSSProperties,
  useMemo,
  useCallback,
} from "react";
import Events from "./components/Events";
import Chart from "./components/Chart";
import Budgets from "./components/Budgets";
import { expenseTypes as defaultExpenseTypes } from "./expenseTypes";
import { incomeTypes as defaultIncomeTypes } from "./incomeTypes";
import Input from "./components/Input";
import { useSynchronization, LS_KEY } from "./useSynchronization";
import Categories from "./components/Categories";
import Table from "./components/Table";

import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import ImportExport from "./components/ImportExport";
import { IoIosWallet } from "react-icons/io";
import { MdEvent, MdSettings, MdShowChart, MdList } from "react-icons/md";
import { useToasts } from "react-toast-notifications";

type UseState<S> = [S, Dispatch<SetStateAction<S>>];

type FioBankResponse = {
  accountStatement: {
    info: {
      accountId: "2601709751";
      bankId: "2010";
      currency: "EUR";
      iban: "CZ4820100000002601709751";
      bic: "FIOBCZPPXXX";
      openingBalance: 545.87;
      closingBalance: 1468.82;
      dateStart: "2020-12-20+0100";
      dateEnd: "2021-01-05+0100";
      yearList: null;
      idList: null;
      idFrom: 23619162821;
      idTo: 23624836018;
      idLastDownload: null;
    };
    transactionList: {
      transaction: Array<{
        [column: string]: {
          id: number;
          value: string | number;
          name: string;
        };
      }>;
    };
  };
};

export const Context = createContext({
  transactions: [[], () => {}] as UseState<
    {
      id: string;
      date: Date;
      value: number;
      attachedTo: {
        id: string;
        type: "EVENT" | "CATEGORY";
      };
      description?: string;
    }[]
  >,
  events: [[], () => {}] as UseState<
    {
      id: string;
      name: string;
      date: Date;
      frequency: "ONCE" | "MONTHLY" | "YEARLY";
    }[]
  >,
  budgets: [[], () => {}] as UseState<
    {
      id: string;
      name: string;
      value: number;
      span: "MONTHLY" | "YEARLY";
      attachedTo: {
        type: "EVENT" | "CATEGORY";
        id: string;
      };
      containing?: string;
    }[]
  >,
  expenseCategories: [[], () => {}] as UseState<
    {
      id: string;
      label: string;
      default?: boolean;
    }[]
  >,
  incomeCategories: [[], () => {}] as UseState<
    {
      id: string;
      label: string;
      default?: boolean;
    }[]
  >,
  currentView: ["INPUT", () => {}] as UseState<
    "INPUT" | "BUDGETS" | "EVENTS" | "CHART" | "TABLE" | "SETTINGS"
  >,
});

const classNameColors = {
  INPUT: "text-indigo-400",
  BUDGETS: "text-yellow-400",
  EVENTS: "text-red-400",
  CHART: "text-gray-500",
  TABLE: "text-green-500",
  SETTINGS: "text-gray-500",
};

const ExpenseTracker: React.FC = () => {
  const transactions = useState<
    {
      id: string;
      date: Date;
      value: number;
      attachedTo: {
        id: string;
        type: "EVENT" | "CATEGORY";
      };
    }[]
  >([]);

  const events = useState<ContextType<typeof Context>["events"][0]>([]);
  const budgets = useState<ContextType<typeof Context>["budgets"][0]>([]);
  const incomeCategories = useState<
    ContextType<typeof Context>["incomeCategories"][0]
  >(defaultIncomeTypes);
  const expenseCategories = useState<
    ContextType<typeof Context>["expenseCategories"][0]
  >(defaultExpenseTypes);

  const lastSyncDate = useSynchronization({
    transactions: transactions[0],
    budgets: budgets[0],
    events: events[0],
    expenseCategories: expenseCategories[0],
    incomeCategories: incomeCategories[0],
  });

  const { t } = useTranslation();

  const currentView = useState<ContextType<typeof Context>["currentView"][0]>(
    "INPUT"
  );

  const views = {
    INPUT: Input,
    BUDGETS: Budgets,
    EVENTS: Events,
    TABLE: useCallback(
      () => (
        <>
          <h1 className="text-3xl mt-10 mb-4">💲 {t("Transactions table")}</h1>
          <Table />
        </>
      ),
      []
    ),
    CHART: useCallback(
      ({ ...props }) => <Chart transactions={transactions[0]} {...props} />,
      [transactions[0]]
    ),
    SETTINGS: useCallback(() => <ImportExport />, []),
  };

  useEffect(() => {
    const stringData = localStorage.getItem(LS_KEY);

    if (stringData !== null) {
      const parsedData = JSON.parse(stringData);
      if (parsedData.transactions) {
        transactions[1](
          parsedData.transactions.map((t) => ({ ...t, date: new Date(t.date) }))
        );
      }
      if (parsedData.events) {
        events[1](
          parsedData.events.map((e) => ({
            ...e,
            date: new Date(e.date),
          }))
        );
      }
      if (parsedData.budgets) {
        budgets[1](parsedData.budgets);
      }
      if (parsedData.incomeCategories) {
        incomeCategories[1](parsedData.incomeCategories);
      }
      if (parsedData.expenseCategories) {
        expenseCategories[1](parsedData.expenseCategories);
      }
    }
  }, []);

  const View = views[currentView[0]];

  const getMenuItemProps = (itemName: typeof currentView[0]) => {
    return {
      className: `bold text-gray-400 bg-white shadow-sm h-22 rounded-xl cursor-pointer transition-all duration-300  ${
        currentView[0] === itemName
          ? `${classNameColors[itemName]} font-bold`
          : ""
      }`,
      style:
        currentView[0] === itemName
          ? ({
              fontSize: 50,
            } as CSSProperties)
          : {},
    };
  };

  return (
    <Context.Provider
      value={{
        transactions,
        budgets,
        events,
        expenseCategories,
        incomeCategories,
        currentView,
      }}
    >
      <div className="p-5 sm:p-8 lg:p-12 bg-gray-200 relative min-h-screen">
        <div className="absolute right-5 top-5">
          <span>{t("Select language")}:</span>
          <select
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
          >
            <option value="en">{t("English")}</option>
            <option value="sk">{t("Slovak")}</option>
          </select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-5 mt-16 mb-10">
          <div
            title="Input"
            onClick={() => currentView[1]("INPUT")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <span {...getMenuItemProps("INPUT")}>+</span>
            <span className="text-xl mt-3">{t("Add")}</span>
          </div>
          <div
            title="Budgets"
            onClick={() => currentView[1]("BUDGETS")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <div {...getMenuItemProps("BUDGETS")}>
              <IoIosWallet />
            </div>
            <span className="text-xl mt-3">{t("Budgets")}</span>
          </div>
          <div
            title="Events"
            onClick={() => currentView[1]("EVENTS")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <div {...getMenuItemProps("EVENTS")}>
              <MdEvent />
            </div>
            <span className="text-xl mt-3">{t("Events")}</span>
          </div>
          <div
            title="Stats"
            onClick={() => currentView[1]("CHART")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <div {...getMenuItemProps("CHART")}>
              <MdShowChart />
            </div>
            <span className="text-xl mt-3">{t("Chart")}</span>
          </div>
          <div
            title="Transactions"
            onClick={() => currentView[1]("TABLE")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <div {...getMenuItemProps("TABLE")}>
              <MdList />
            </div>
            <span className="text-xl mt-3">{t("Table")}</span>
          </div>
          <div
            title="Settings"
            onClick={() => currentView[1]("SETTINGS")}
            className="flex flex-col items-center justify-center text-4xl bold text-gray-400 bg-white shadow-sm h-44 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
          >
            <div {...getMenuItemProps("SETTINGS")}>
              <MdSettings />
            </div>
            <span className="text-xl mt-3">{t("Settings")}</span>
          </div>
        </div>
        <View />

        {lastSyncDate && (
          <div className="mt-20 mb-4 text-sm ">
            {t("Last saved data")}: {lastSyncDate.toLocaleString()}
          </div>
        )}
      </div>
    </Context.Provider>
  );
};

export default ExpenseTracker;
